<template>
  <transition name="fade">
    <div v-if="dialog" class="full-screen-overlay" ref="el">
      <div ref="fsd" class="full-screen-overlay-scroll-area" @scroll="onScroll">
        <!-- <slot
          name="default"
          :current-index="currentIndex"
          :visible-index-range="visibleIndexRange"
        ></slot> -->
        <slot name="default" />
      </div>

      <div
        class="absolute inset-y-0 left-0 w-16 flex items-center justify-center hide-on-touch"
      >
        <button @click="scrollLeft">
          <ChevronLeftIcon class="w-16 h-16" />
        </button>
      </div>

      <div
        class="absolute inset-y-0 right-0 w-16 flex items-center justify-center hide-on-touch"
      >
        <button @click="scrollRight">
          <ChevronRightIcon class="w-16 h-16" />
        </button>
      </div>

      <div class="full-screen-overlay-cancel-bar">
        <slot name="download"></slot>

        <!-- X button to close dialog -->
        <button @click="dialog = false">
          <XMarkIcon class="w-8 h-8" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
type TFullscreenInject = {
  visibleIndexRange: ComputedRef<[number, number]>;
};

export const injectionKey: InjectionKey<TFullscreenInject> = Symbol(
  "TFullscreenInjectInjectKey"
);
</script>

<script lang="ts" setup>
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/20/solid";

const dialog = defineModel<boolean>({ default: false });
const currentIndex = defineModel("index", { type: Number, required: true });

const props = defineProps({
  itemCount: {
    type: Number as PropType<number>,
    required: true,
  },
});

const emit = defineEmits<{ download: [] }>();

const { itemCount } = toRefs(props);

const visibleIndexRange = computed(
  () => [currentIndex.value - 1, currentIndex.value + 2] as [number, number]
);

provide(injectionKey, { visibleIndexRange });

const fsd: Ref<HTMLDivElement | null> = ref(null);
const el: Ref<HTMLDivElement | null> = ref(null);

// const childCount = () => fsd.value?.children.length ?? 0;
const elementWidth = () => el.value?.clientWidth ?? 0;

const scrollToIndex = (index: number, smooth = true) => {
  const newIndex = Math.min(Math.max(0, index), itemCount.value - 1);

  currentIndex.value = newIndex;

  if (fsd.value) {
    // Seems to be a short delay brefore currentIndex is propogated and updated.  Hence, we use newIndex below
    // const offset = currentIndex.value * elementWidth();
    const offset = newIndex * elementWidth();

    // https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollBy#examples
    const behavior = smooth ? "smooth" : "instant";

    fsd.value.scrollTo({
      left: offset,
      top: 0,
      behavior,
    });
  }
};

const scrollLeft = () => scrollToIndex(currentIndex.value - 1);
const scrollRight = () => scrollToIndex(currentIndex.value + 1);

/**
 * This is useful when swiping left and right.
 */
const onScroll = (event: any) => {
  const target = event.target; //.scrollLeft

  if (target) {
    currentIndex.value = Math.round(target.scrollLeft / elementWidth());
  }
};

onKeyStroke("ArrowLeft", (e) => {
  e.preventDefault();
  scrollLeft();
});

onKeyStroke("ArrowRight", (e) => {
  e.preventDefault();
  scrollRight();
});

onKeyStroke("Escape", (e) => {
  e.preventDefault();
  dialog.value = false;
});

watch(
  dialog,
  async (value) => {
    if (value && import.meta.client) {
      await nextTick();
      scrollToIndex(currentIndex.value, false);
    }
  },
  { immediate: true }
);
</script>

<style lang="postcss">
body.disable-body-scroll {
  @apply overflow-hidden;
}
</style>

<style scoped lang="postcss">
.full-screen-overlay {
  @apply bg-black text-white;
  @apply fixed inset-0;
  height: 100dvh;
  width: 100vw;
}

.full-screen-overlay-scroll-area {
  @apply flex overflow-y-hidden overflow-x-auto snap-x snap-mandatory;
}

.full-screen-overlay-cancel-bar {
  @apply absolute inset-x-0 top-0 flex items-center justify-end p-4 gap-4;
}

@media (hover: none) {
  .hide-on-touch {
    @apply hidden;
  }
}
</style>
